'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import { CreditCard, Loader2, Shield } from 'lucide-react'

declare global {
  interface Window {
    CollectJS: {
      configure: (config: unknown) => void
    }
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          action?: string
          callback?: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
        },
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
  }
}

type BuyCourseOption = {
  title: string
  description?: string
  slug: string
  isRecurring?: boolean
  nmiPlanId?: string
  nmiProductId?: string
  priceCents: number
  imageUrl?: string
  imageAlt?: string
}

export default function BuyCheckoutForm({
  monthly,
  annual,
  currentUser,
  initialCycle = 'monthly',
  initialPromoCode = '',
}: {
  monthly?: BuyCourseOption
  annual?: BuyCourseOption
  currentUser?: {
    name?: string
    email?: string
    nmiCustomerId?: string
  } | null
  initialCycle?: 'monthly' | 'annual'
  initialPromoCode?: string
}) {
  const router = useRouter()

  const availableCycles = useMemo(
    () =>
      [monthly ? ('monthly' as const) : null, annual ? ('annual' as const) : null].filter(
        Boolean,
      ) as Array<'monthly' | 'annual'>,
    [monthly, annual],
  )

  const resolvedInitialCycle: 'monthly' | 'annual' = availableCycles.includes(initialCycle)
    ? initialCycle
    : (availableCycles[0] ?? 'monthly')

  const [selectedCycle, setSelectedCycle] = useState<'monthly' | 'annual'>(resolvedInitialCycle)
  const selectedOption = selectedCycle === 'annual' ? annual : monthly

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [collectJsLoaded, setCollectJsLoaded] = useState(false)
  const [captchaReady, setCaptchaReady] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [billingName, setBillingName] = useState(currentUser?.name || '')
  const [billingEmail, setBillingEmail] = useState(currentUser?.email || '')
  const [billingZip, setBillingZip] = useState('')
  const [showPromoCode, setShowPromoCode] = useState(Boolean(initialPromoCode))
  const [promoCode, setPromoCode] = useState(String(initialPromoCode || '').toUpperCase())
  const [promoCodePendingPlanValidation, setPromoCodePendingPlanValidation] = useState('')
  const [promoApplying, setPromoApplying] = useState(false)
  const [promoAppliedCode, setPromoAppliedCode] = useState<string | null>(null)
  const [promoDiscountCents, setPromoDiscountCents] = useState(0)
  const [promoAmountCents, setPromoAmountCents] = useState<number | null>(null)
  const [promoFeedback, setPromoFeedback] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)
  const [showPromoEmailModal, setShowPromoEmailModal] = useState(false)

  const captchaTokenRef = useRef('')
  const configuredRef = useRef(false)
  const submitPurchaseRef = useRef<(paymentToken: string) => Promise<void>>(async () => {})
  const submitInFlightRef = useRef(false)
  const captchaContainerRef = useRef<HTMLDivElement | null>(null)
  const captchaWidgetIdRef = useRef<string | null>(null)

  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
  const effectiveAmountCents = promoAmountCents ?? selectedOption?.priceCents ?? 0

  useEffect(() => {
    const fallbackCycle = availableCycles[0]
    if (!selectedOption && fallbackCycle) {
      setSelectedCycle(fallbackCycle)
    }
  }, [selectedOption, availableCycles])

  const resetCaptcha = useCallback(() => {
    captchaTokenRef.current = ''
    setCaptchaToken('')
    if (captchaWidgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(captchaWidgetIdRef.current)
      } catch {}
    }
  }, [])

  useEffect(() => {
    const tokenizationKey = process.env.NEXT_PUBLIC_NMI_TOKENIZATION_KEY
    if (!tokenizationKey) {
      setMessage({
        type: 'error',
        text: 'Payment system configuration error. Please contact support.',
      })
      return
    }

    if (typeof window === 'undefined') return
    if (window.CollectJS) {
      setCollectJsLoaded(true)
      return
    }

    const existing = document.querySelector('script[src="https://secure.nmi.com/token/Collect.js"]')
    if (existing) {
      if (window.CollectJS) {
        setCollectJsLoaded(true)
      } else {
        existing.addEventListener('load', () => setCollectJsLoaded(true), { once: true })
      }
      return
    }

    const script = document.createElement('script')
    script.src = 'https://secure.nmi.com/token/Collect.js'
    script.setAttribute('data-tokenization-key', tokenizationKey)
    script.onload = () => setCollectJsLoaded(true)
    script.onerror = () => {
      setMessage({
        type: 'error',
        text: 'Failed to load payment system. Please refresh and try again.',
      })
    }
    document.head.appendChild(script)
  }, [])

  useEffect(() => {
    if (!turnstileSiteKey || typeof window === 'undefined') return
    if (window.turnstile) {
      setCaptchaReady(true)
      return
    }

    const existing = document.querySelector(
      'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"]',
    )
    if (existing) {
      existing.addEventListener('load', () => setCaptchaReady(true), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => setCaptchaReady(true)
    script.onerror = () => {
      setMessage({
        type: 'error',
        text: 'Unable to load human verification. Please refresh and try again.',
      })
    }
    document.head.appendChild(script)
  }, [turnstileSiteKey])

  useEffect(() => {
    if (!turnstileSiteKey || !captchaReady || !captchaContainerRef.current) return
    if (typeof window === 'undefined' || !window.turnstile) return
    if (captchaWidgetIdRef.current) return

    captchaWidgetIdRef.current = window.turnstile.render(captchaContainerRef.current, {
      sitekey: turnstileSiteKey,
      action: 'checkout',
      callback: (token: string) => {
        const nextToken = String(token || '')
        captchaTokenRef.current = nextToken
        setCaptchaToken(nextToken)
      },
      'error-callback': () => {
        captchaTokenRef.current = ''
        setCaptchaToken('')
        setMessage({ type: 'error', text: 'Human verification failed. Please try again.' })
      },
      'expired-callback': () => {
        captchaTokenRef.current = ''
        setCaptchaToken('')
      },
      theme: 'dark',
    })
  }, [captchaReady, turnstileSiteKey])

  const resolveSitenameHint = (): string | undefined => {
    if (typeof window === 'undefined') return undefined

    const fromQuery = String(
      new URLSearchParams(window.location.search).get('sitename') ||
        new URLSearchParams(window.location.search).get('sn') ||
        '',
    )
      .trim()
      .toLowerCase()

    if (fromQuery) return fromQuery

    const host = String(window.location.hostname || '')
      .trim()
      .toLowerCase()
    const parts = host.split('.')
    if (parts.length < 3) return undefined
    if (parts[0] === 'www') return undefined
    return parts[0] || undefined
  }

  const clearAppliedPromo = () => {
    setPromoAppliedCode(null)
    setPromoDiscountCents(0)
    setPromoAmountCents(null)
    setPromoFeedback(null)
  }

  const removeAppliedPromo = () => {
    setPromoCodePendingPlanValidation('')
    clearAppliedPromo()
  }

  const buildPromoSuccessMessage = (opts: {
    discountCents: number
    isRecurring: boolean
    chargesToApply: number
    billingIntervalLabel?: string | null
  }) => {
    const savings = formatPrice(opts.discountCents)
    if (!opts.isRecurring) return `Promo code applied. You save ${savings}.`

    const interval = opts.billingIntervalLabel || 'recurring'
    if (opts.chargesToApply === 0) {
      return `Promo code applied. You save ${savings} on every ${interval} charge.`
    }
    if (opts.chargesToApply === 1) {
      return `Promo code applied. You save ${savings} on your first ${interval} charge.`
    }
    return `Promo code applied. You save ${savings} for your first ${opts.chargesToApply} ${interval} charges.`
  }

  const applyPromoCode = async (opts?: {
    silent?: boolean
    emailOverride?: string
    codeOverride?: string
  }) => {
    const option = selectedOption
    const code = String(opts?.codeOverride || promoCode)
      .trim()
      .toUpperCase()
    if (!option || !code) {
      if (!opts?.silent) setPromoFeedback({ type: 'error', text: 'Enter a promo code first.' })
      clearAppliedPromo()
      return
    }

    setPromoApplying(true)
    if (!opts?.silent) setPromoFeedback(null)
    try {
      const response = await fetch('/api/nmi/course-promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          courseSlug: option.slug,
          promoCode: code,
          billingEmail: String(opts?.emailOverride ?? billingEmail).trim(),
        }),
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result?.success) {
        clearAppliedPromo()
        setPromoCodePendingPlanValidation('')
        if (result?.requiresEmail && !billingEmail.trim()) {
          setShowPromoEmailModal(true)
          setPromoFeedback({
            type: 'error',
            text: 'Enter your email address to validate this promo code.',
          })
          return
        }
        setPromoFeedback({
          type: 'error',
          text: String(result?.message || 'Promo code is invalid.'),
        })
        return
      }

      const amountCents = Math.max(0, Number(result?.amountCents || 0))
      const discountCents = Math.max(0, Number(result?.discountCents || 0))
      const chargesToApply = Math.max(0, Number(result?.chargesToApply || 0))
      const isRecurring = Boolean(result?.isRecurring)
      const billingIntervalLabel = result?.billingIntervalLabel
        ? String(result.billingIntervalLabel)
        : null
      const applied = String(result?.promoCode || code).toUpperCase()
      setPromoCode(applied)
      setPromoCodePendingPlanValidation(applied)
      setPromoAppliedCode(applied)
      setPromoDiscountCents(discountCents)
      setPromoAmountCents(amountCents)
      setPromoFeedback({
        type: 'success',
        text: buildPromoSuccessMessage({
          discountCents,
          isRecurring,
          chargesToApply,
          billingIntervalLabel,
        }),
      })
    } catch {
      clearAppliedPromo()
      setPromoCodePendingPlanValidation('')
      setPromoFeedback({ type: 'error', text: 'Unable to validate promo code right now.' })
    } finally {
      setPromoApplying(false)
    }
  }

  useEffect(() => {
    if (!initialPromoCode || !selectedOption?.slug) return
    void applyPromoCode()
  }, [])

  useEffect(() => {
    const codeToValidate = promoAppliedCode || promoCodePendingPlanValidation
    if (!codeToValidate || !selectedOption?.slug) return
    setPromoCode(codeToValidate)
    setPromoAppliedCode(null)
    setPromoDiscountCents(0)
    setPromoAmountCents(null)
    setPromoFeedback({ type: 'success', text: 'Validating promo code for selected plan...' })
    void applyPromoCode({ silent: true, codeOverride: codeToValidate })
  }, [selectedCycle])

  const submitPurchase = async (paymentToken: string) => {
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true
    const option = selectedOption
    if (!option) {
      setMessage({ type: 'error', text: 'No purchase option is currently available.' })
      submitInFlightRef.current = false
      return
    }

    const resolvedBillingName = billingName.trim()
    const resolvedBillingEmail = billingEmail.trim()
    const resolvedBillingZip = billingZip.trim()

    if (!resolvedBillingName || !resolvedBillingEmail || !resolvedBillingZip) {
      setMessage({ type: 'error', text: 'Please fill in all required billing fields.' })
      submitInFlightRef.current = false
      return
    }

    if (option.isRecurring && !String(option.nmiPlanId || '').trim()) {
      setMessage({
        type: 'error',
        text: 'This recurring plan is not fully configured. Please contact support.',
      })
      submitInFlightRef.current = false
      return
    }

    const resolvedCaptchaToken = String(captchaTokenRef.current || '').trim()
    if (turnstileSiteKey && !resolvedCaptchaToken) {
      setMessage({ type: 'error', text: 'Please complete human verification before checkout.' })
      submitInFlightRef.current = false
      return
    }

    setLoading(true)
    setMessage(null)

    const sitenameHint = resolveSitenameHint()

    try {
      const isRecurring = Boolean(option.isRecurring)
      const amount = Number(effectiveAmountCents || 0)
      const normalizedEmail = resolvedBillingEmail.toLowerCase()
      const recurringPlanId = String(option.nmiPlanId || '').trim()
      const oneTimeProductId = String(option.nmiProductId || option.slug || '').trim()

      const intentResponse = await fetch('/api/nmi/checkout-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow: isRecurring ? 'subscribe' : 'purchase',
          context: isRecurring
            ? {
                planId: recurringPlanId,
                planPrice: amount,
                billingEmail: normalizedEmail,
                ...(promoAppliedCode ? { promoCode: promoAppliedCode } : {}),
              }
            : {
                productType: 'course',
                productId: oneTimeProductId,
                amount,
                billingEmail: normalizedEmail,
                ...(promoAppliedCode ? { promoCode: promoAppliedCode } : {}),
              },
          captchaToken: resolvedCaptchaToken,
        }),
      })

      const intentResult = await intentResponse.json().catch(() => ({}))
      if (!intentResponse.ok || !intentResult?.checkoutIntentToken) {
        resetCaptcha()
        setMessage({
          type: 'error',
          text: String(intentResult?.message || 'Unable to start secure checkout session.'),
        })
        return
      }

      const checkoutIntentToken = String(intentResult.checkoutIntentToken)

      const response = isRecurring
        ? await fetch('/api/nmi/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              planId: recurringPlanId,
              planPrice: amount,
              courseSlug: option.slug,
              billingName: resolvedBillingName,
              billingZip: resolvedBillingZip,
              billingEmail: resolvedBillingEmail,
              paymentToken,
              captchaToken: resolvedCaptchaToken,
              checkoutIntentToken,
              customerVaultId: currentUser?.nmiCustomerId,
              ...(promoAppliedCode ? { promoCode: promoAppliedCode } : {}),
              ...(sitenameHint ? { sitenameHint } : {}),
              plan: {
                title: option.title,
              },
            }),
          })
        : await fetch('/api/nmi/purchase', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              productType: 'course',
              productId: oneTimeProductId,
              courseSlug: option.slug,
              productTitle: option.title,
              amount,
              billingName: resolvedBillingName,
              billingZip: resolvedBillingZip,
              billingEmail: resolvedBillingEmail,
              paymentToken,
              captchaToken: resolvedCaptchaToken,
              checkoutIntentToken,
              ...(promoAppliedCode ? { promoCode: promoAppliedCode } : {}),
              ...(sitenameHint ? { sitenameHint } : {}),
            }),
          })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result?.success) {
        resetCaptcha()
        setMessage({
          type: 'error',
          text: String(result?.message || 'Payment failed. Please try again.'),
        })
        return
      }

      setMessage({
        type: 'success',
        text: String(
          result?.message ||
            (isRecurring
              ? 'Subscription activated successfully!'
              : 'Purchase completed successfully!'),
        ),
      })

      setBillingName('')
      setBillingEmail('')
      setBillingZip('')
      resetCaptcha()

      try {
        const maybeCollectJs = window.CollectJS as unknown as { clearInputs?: () => void }
        maybeCollectJs?.clearInputs?.()
      } catch {}

      setTimeout(() => {
        router.push('/dashboard')
      }, 1200)
    } catch {
      resetCaptcha()
      setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' })
    } finally {
      submitInFlightRef.current = false
      setLoading(false)
    }
  }

  useEffect(() => {
    submitPurchaseRef.current = submitPurchase
  }, [submitPurchase])

  useEffect(() => {
    if (!collectJsLoaded || configuredRef.current) return
    if (typeof window === 'undefined' || !window.CollectJS) return

    try {
      window.CollectJS.configure({
        paymentSelector: '#buy-submit-button',
        variant: 'inline',
        fields: {
          ccnumber: {
            selector: '#buy-cc-number',
            title: 'Card Number',
            placeholder: '0000 0000 0000 0000',
          },
          ccexp: {
            selector: '#buy-cc-exp',
            title: 'Card Expiration',
            placeholder: 'MM / YY',
          },
          cvv: {
            display: 'show',
            selector: '#buy-cc-cvv',
            title: 'CVV Code',
            placeholder: 'CVV',
          },
        },
        callback: (response: { token?: string; error?: string }) => {
          if (response?.token) {
            void submitPurchaseRef.current(String(response.token))
            return
          }
          setMessage({
            type: 'error',
            text: String(
              response?.error || 'Payment token generation failed. Please check your card details.',
            ),
          })
        },
      })
      configuredRef.current = true
    } catch {
      setMessage({
        type: 'error',
        text: 'Payment form initialization failed. Please refresh and try again.',
      })
    }
  }, [collectJsLoaded])

  const formatPrice = (cents: number) => `$${(Math.max(0, cents) / 100).toFixed(2)}`
  const hasRequiredBillingInfo =
    Boolean(billingName.trim()) && Boolean(billingEmail.trim()) && Boolean(billingZip.trim())
  const hasRequiredCaptcha = !turnstileSiteKey || Boolean(captchaToken.trim())
  const canSubmit =
    !loading &&
    Boolean(collectJsLoaded) &&
    Boolean(selectedOption) &&
    hasRequiredBillingInfo &&
    hasRequiredCaptcha

  if (!monthly && !annual) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        Buy options are not configured yet. Please set the monthly and/or annual Drive Mastery
        course slugs in Site Settings.
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-2xl rounded-2xl border border-border bg-card p-6 md:p-8 shadow-xl">
      <div className="mb-6 text-center">
        <h2 className="text-2xl font-semibold">Complete Your Purchase</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose a plan and enter your payment details below.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-2 rounded-xl border border-border bg-muted/30 p-2">
        <button
          type="button"
          disabled={!monthly}
          onClick={() => setSelectedCycle('monthly')}
          className={cn(
            'rounded-lg px-3 py-2 text-sm font-medium text-center transition-colors',
            selectedCycle === 'monthly' ? 'bg-[#0B4C80] text-white' : 'text-muted-foreground',
            !monthly && 'cursor-not-allowed opacity-50',
          )}
        >
          <div>Monthly</div>
          <div className="mt-0.5 text-xs opacity-80">
            {monthly ? formatPrice(monthly.priceCents) : 'Unavailable'}
          </div>
        </button>
        <button
          type="button"
          disabled={!annual}
          onClick={() => setSelectedCycle('annual')}
          className={cn(
            'rounded-lg px-3 py-2 text-sm font-medium text-center transition-colors',
            selectedCycle === 'annual' ? 'bg-[#0B4C80] text-white' : 'text-muted-foreground',
            !annual && 'cursor-not-allowed opacity-50',
          )}
        >
          <div>Annual</div>
          <div className="mt-0.5 text-xs opacity-80">
            {annual ? formatPrice(annual.priceCents) : 'Unavailable'}
          </div>
        </button>
      </div>

      {selectedOption?.imageUrl ? (
        <div className="mb-6 overflow-hidden rounded-xl border border-border bg-muted/10">
          <img
            src={selectedOption.imageUrl}
            alt={selectedOption.imageAlt || selectedOption.title || 'Course image'}
            className="h-44 w-full object-cover md:h-52"
          />
        </div>
      ) : null}

      <div className="relative mb-6 rounded-xl border border-border bg-muted/20 p-4 text-center">
        <div className="text-lg font-semibold">{selectedOption?.title || 'Unavailable option'}</div>
        {selectedOption?.description ? (
          <p className="mt-1 text-sm text-muted-foreground">{selectedOption.description}</p>
        ) : null}
        <div className="mt-3 flex justify-center">
          <div>
            {promoAppliedCode && promoDiscountCents > 0 ? (
              <div className="text-sm text-muted-foreground line-through">
                {formatPrice(selectedOption?.priceCents || 0)}
              </div>
            ) : null}
            <span className="text-3xl font-bold">{formatPrice(effectiveAmountCents)}</span>
          </div>
        </div>
        <span className="absolute bottom-4 right-4 text-xs text-muted-foreground">
          {selectedOption?.isRecurring ? 'Recurring' : 'One-time'}
        </span>
      </div>

      <form id="buy-payment-form" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Full Name</label>
            <input
              name="billingName"
              type="text"
              required
              value={billingName}
              onChange={(event) => setBillingName(event.target.value)}
              placeholder="Enter your full name"
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label className="block text-sm font-medium">Email</label>
              {!showPromoCode ? (
                <button
                  type="button"
                  className="text-[11px] font-medium text-[#0B4C80]/80 hover:text-[#0B4C80] hover:underline"
                  onClick={() => setShowPromoCode(true)}
                >
                  Add promo code
                </button>
              ) : null}
            </div>
            <input
              name="billingEmail"
              type="email"
              required
              value={billingEmail}
              onChange={(event) => setBillingEmail(event.target.value)}
              placeholder="Enter your email"
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            />
          </div>

          {showPromoCode ? (
            <div className="space-y-1 md:col-span-2">
              {promoAppliedCode ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-green-400/40 bg-green-500/10 px-3 py-1 text-xs text-green-200">
                  <span>{promoAppliedCode}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setPromoCode('')
                      removeAppliedPromo()
                    }}
                    className="text-green-100 hover:text-white"
                    aria-label="Remove promo code"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(event) => {
                      setPromoCode(event.target.value.toUpperCase())
                      clearAppliedPromo()
                    }}
                    placeholder="Promo code"
                    className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={promoApplying || !promoCode.trim()}
                    onClick={() => void applyPromoCode()}
                  >
                    {promoApplying ? 'Applying...' : 'Apply'}
                  </Button>
                </div>
              )}
              {promoFeedback ? (
                <p
                  className={cn(
                    'text-[11px]',
                    promoFeedback.type === 'success' ? 'text-green-300' : 'text-destructive',
                  )}
                >
                  {promoFeedback.text}
                </p>
              ) : null}
            </div>
          ) : null}

          <div>
            <label className="mb-1 block text-sm font-medium">ZIP Code</label>
            <input
              name="billingZip"
              type="text"
              required
              value={billingZip}
              onChange={(event) => setBillingZip(event.target.value)}
              placeholder="Enter your ZIP code"
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Card Number</label>
            <div
              id="buy-cc-number"
              className="min-h-[42px] rounded-lg border border-border bg-background px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Expiry Date</label>
            <div
              id="buy-cc-exp"
              className="min-h-[42px] rounded-lg border border-border bg-background px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">CVV</label>
            <div
              id="buy-cc-cvv"
              className="min-h-[42px] rounded-lg border border-border bg-background px-3 py-2"
            />
          </div>
        </div>

        {turnstileSiteKey ? (
          <div>
            <label className="mb-1 block text-sm font-medium">Human Verification</label>
            <div className="flex justify-center">
              <div ref={captchaContainerRef} />
            </div>
            {!captchaToken ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Complete verification to enable checkout.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
          <Shield size={16} className="mt-0.5 shrink-0" />
          <p>Your payment information is securely tokenized and never stored on our servers.</p>
        </div>

        {message ? (
          <div
            className={cn(
              'rounded-lg border p-3 text-sm',
              message.type === 'success'
                ? 'border-green-500/40 bg-green-500/10 text-green-200'
                : 'border-destructive/40 bg-destructive/10 text-destructive',
            )}
          >
            {message.text}
          </div>
        ) : null}

        <Button
          id="buy-submit-button"
          type="button"
          className="w-full disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canSubmit}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Processing...
            </>
          ) : !collectJsLoaded ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Loading payment form...
            </>
          ) : (
            <>
              <CreditCard size={16} />
              {selectedOption?.isRecurring ? 'Start Subscription' : 'Buy Now'} ·{' '}
              {formatPrice(effectiveAmountCents)}
            </>
          )}
        </Button>
      </form>
      {showPromoEmailModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl">
            <h3 className="text-lg font-semibold">Email Required</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your email address to continue validating this promo code.
            </p>
            <input
              type="email"
              value={billingEmail}
              onChange={(event) => setBillingEmail(event.target.value)}
              placeholder="you@example.com"
              className="mt-4 w-full rounded-lg border border-border bg-background px-3 py-2"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowPromoEmailModal(false)
                  setPromoFeedback({ type: 'error', text: 'Promo code is invalid.' })
                }}
              >
                Dismiss
              </Button>
              <Button
                type="button"
                disabled={!billingEmail.trim() || promoApplying}
                onClick={() => {
                  setShowPromoEmailModal(false)
                  void applyPromoCode({ emailOverride: billingEmail })
                }}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
